<template>
	<!-- 
		# MainCard

		V.1.3

		Limpieza general y optimizaciones menores

		V.1.2

		Buscador automático
		
		V.1.1

		Ahora la configuración de origen de datos y origen de índice, 
		junto a la coniguración de campos, se define fuera del objeto de exportación

		V.1.0

		Para la integración de imágenes requiere 
		**img-input-autoload** configurado en elements y
		el controlador UiController.php y UiModel.php en la API Base. **Además hace uso del 
		paginador para javascript implementado en PaginateController.php**

		Para la actualización de datos hace uso de StorageController.php 

		@Author Daniel Lepe 2021
	-->

	<b-card id="RecetasMainCard" no-body>

		<!-- CARD HEADER -->
		<b-row align-h="end" slot="header">
			<b-col>
				<div tabindex="-1" role="group">
					<div role="group" class="input-group">
						<input type="text" v-model="term" @keyup.enter="refresh()" :placeholder="labelsCfg.searchPlaceholder"
							class="form-control" />
						<div class="input-group-append" v-if="!!term">
							<b-button type="button" variant="light" @click="term = null; refresh()">
								<i class="fa fa-times"></i>
							</b-button>
							<b-button type="button" :variant="variant" @click="refresh()">
								<i class="fa fa-search"></i>
							</b-button>
						</div>
					</div>
				</div>
			</b-col>
			<b-col md="4">
				<b-button block :variant="variant" @click="$refs.recetaMainModal.openForNew()" class="mb-0"> {{labelsCfg.newLabel}} </b-button>
			</b-col>
		</b-row>
		<!-- CARD HEADER -->

		<spinner v-if="isLoading"/>

		<!-- DATA TEMPLATE -->
		<template v-else-if="parseInt(data.count)">
			<b-table :fields="fields" responsive striped class="mb-0" :items="items">
					
					<!-- START TEMPLATES BASICOS DE DATOS  -->
					<template #cell(visibilidad)="data">
						<div class="visibilidad" title="" v-b-popover.hover="'Solo se muestran en las listas de los usuarios las recetas publicas o temporales, pero las ocultas en listas requieren un HASH para ser visibles.'">
							<!-- {{data.item.compartir}} -->
							{{getCompartirStatus(data.item.compartir)}}
							<!-- <tiny-check :value="data.value" /> -->
						</div>
					</template>
					<template #cell(duraciones)="data">
						<div class="duracion">
							<i class="fa fa-clock-o"></i>
							{{moment().hour(0).minute(0).add((parseInt(data.item.tiempo_coccion) || 0) + (parseInt(data.item.tiempo_preparacion) || 0), 'minute').format('HH:mm')}}
						</div>
					</template>
					<template #cell(desayuno)="data">
						<tiny-check :value="data.value" />
					</template>
					<template #cell(comida)="data">
						<tiny-check :value="data.value" />
					</template>
					<template #cell(cena)="data">
						<tiny-check :value="data.value" />
					</template>
					<template #cell(created)="data">{{data.value | humanizar_fecha}} <br> <small>(Última actualizacion {{data.item.modified | humanizar_diferencia}})</small> </template>
					<template #cell(modified)="data">{{data.value | humanizar_diferencia}} <br> <small>(Última actualizacion {{data.item.modified | humanizar_diferencia}})</small> </template>
					<!-- END TEMPLATES BASICOS DE DATOS -->

					<!-- START OF AVATAR SAMPLES -->
					<template #cell(receta)="data">
						<img-portal :src="data.item.imagen" />
						{{data.item.nombre}}
					</template>
					<!-- END OF AVATAR SAMPLES -->

					<!-- START OF BOOLEAN SAMPLE -->
					<template #cell(disponible)="data">
						<div class="align-center">
							<!-- <b-button size="sm" variant="primary" v-if="data.value == '1'"> -->
								<i v-if="data.value == '1'" class="fa text-primary fa-check"></i>
							<!-- </b-button> -->
							<!-- <b-button size="sm" v-else> -->
								<i v-else class="fa fa-minus"></i>
							<!-- </b-button> -->
						</div>
					</template>
					<!-- END OF BOOLEAN SAMPLE -->

					<!-- START OF TOOLING -->
					<template #cell(_)="data">
						<b-button @click="$refs.recetaMainModal.loadItem(data.item.id_pk)" size="sm" :variant="variant"> <i class="fa fa-fw fa-cog"></i> </b-button>
					</template>
					<!-- END OF TOOLING -->

			</b-table>

			<b-row slot="footer" align-v="center">

				<b-col>
					<strong>Página {{data.page}} de {{Math.ceil(data.count / data.per_page)}}</strong> 
					(Se muestran <strong>{{items.length}} registros</strong> 
					de un total de {{data.count}})
				</b-col>

				<b-col>
					<b-pagination
						class="mt-3"
						v-model="data.page"
						:total-rows="data.count"
						:per-page="data.per_page"
						size="sm"
						align="right"
						prev-text="Anterior"
						next-text="Siguiente"
					/>
				</b-col>
			</b-row>
		</template>
		<!-- DATA TEMPLATE -->

		<!-- EMPTY JUMBOTRON -->
		<b-jumbotron class="mb-0" v-else>
			<template #header>{{labelsCfg.emptyHeader}}</template>
			<template #lead>{{labelsCfg.emptyLead}}</template>
		</b-jumbotron>
		<!-- EMPTY JUMBOTRON -->

		<recetaMainModal ref="recetaMainModal" />

	</b-card>

</template>

<script>
	import axios from 'axios'
	import _ from 'lodash'
	import fkSelect from "@/views/elements/fk-select.vue";
	import imgInputAutoload from "@/views/elements/img-input-autoload.vue"
	import moment from 'moment'

	import recetaMainModal from './RecetaMainModal.vue'
import { mapGetters } from 'vuex';

	let labelsCfg = {
		emptyHeader: 'Lista vacía',
		emptyLead: '',
		searchPlaceholder: 'Buscador...',
		newLabel: 'Nueva receta',
		btnDelete: 'Eliminar',
		btnCancel: 'Cancelar',
		btnApply: 'Aplicar',
		btnIsProcessing: 'Procesando...',
	}

	let indexSource = 'nutricion/recetas'; // Para indexar; 
		

	let fields = [ // DEJAR VACÍO PARA MOSTRAR TODO
		{key: 'receta'} ,
		{key: 'visibilidad'} ,
		{key: 'desayuno'} ,
		{key: 'comida'} ,
		{key: 'cena'} ,
		{key: 'duraciones', label: 'Duración'},
		{key: 'origen_calculo_nutricional', label: 'Origen de cálculo'},
		{key: '_'} 
	]
	
	export default {
		name: 'RecetasMainCard',
		props: {},
		components: {
			fkSelect, imgInputAutoload, recetaMainModal
		},
		computed: {
			...mapGetters({
				uiGetCompartirConf: 'uiGetCompartirConf'
			}),
			items(){
				return _.map(this.data.rows, item => {
					item._ = ' '
					return item
				})
			},

			// COMPUTA LA URI
			sourceURI() {
				let init = `${this.indexSource}/${this.data.per_page}/${this.data.page}`;
				if (!!this.term) {
					init += "/" + this.term;
				} else if (!this.term || this.term == "s") {
					init += "/@";
				}
				return init;
			},
		},
		data: () => {
			return {
				moment,
				isLoading: false,
				isProcessing: false,
				term: null,
				variant: "primary",
				indexSource,
				fields,
				labelsCfg: _.cloneDeep(labelsCfg),
				data: {
					rows: [],
					count: 0,
					per_page: 25,
					page: 1
				}
			}
		},
		methods: {
			getCompartirStatus(value){
				let found = _.find(this.uiGetCompartirConf, {value})
				return  found.text || '--'
			},
			refresh() {
				this.isLoading = true;
				this.loadUp().then(() => {
					this.isLoading = false;
				});
			},
			loadUp(){
				return new Promise(success => {
					axios.get(this.sourceURI).then(response => {
						if(typeof response.data.rows != undefined){
							this.data.rows = response.data.rows
							this.data.count = response.data.count
							success()
						}
					})
				})
			}
		},
		mounted() {
			this.isLoading = true;
			this.loadUp().then(()=>{
				this.isLoading = false;
			})
		},
		watch: {
			'data.page'(){
				this.isLoading = true;
				this.loadUp().then(()=>{
					this.isLoading = false;
				})
			}
		}
	}

</script>

<style lang="less">
	#RecetasMainCard {
		.duracion {
			transform: scale(1.5);
		}
		.table {
			thead  {
				th {
					text-align: center;
					&:first-child {
						text-align: left;
					}
					&:last-child {
						text-align: right;
					}
				}
			}
			tbody {
				td {
					text-align: center;
					&:first-child {
						text-align: left;
					}
					&:last-child {
						text-align: right;
					}
				}
			}
		}
	}
</style>